import lodashGet from 'lodash/get';
import { api as masterApi } from '@/api/api';
import { apis, inWindow, isElectron } from '@/config';
import bridgeProxyConfigList from './config/config';
import { workerBridgeInstance } from './instance/workerBridge';
import { masterBridgeInstance } from './instance/masterBridge';
import interceptorConfigMap from './interceptors/index';
const getWinEnv = () => {
    if (!inWindow()) {
        return 'frontPage';
    }
    if (window.isAccountBg) {
        return 'accountBg';
    }
    if (window.isBridgeWorker) {
        return 'dataBg';
    }
    return 'frontPage';
};
const proxyInitLockMap = new Map();
const initBridgeProxyConfig = (masterDriver, configItem) => {
    if (proxyInitLockMap.get(`${configItem.namespace}-proxy`)) {
        return;
    }
    proxyInitLockMap.set(`${configItem.namespace}-proxy`, true);
    const currentEnv = getWinEnv();
    const systemApi = masterApi.getSystemApi();
    const accountApi = masterApi.requireLogicalApi(apis.accountApiImpl);
    if (currentEnv === 'dataBg' || !inWindow()) {
        return;
    }
    const { apis: apiNames, target: targetCallback, namespace, proxyTarget: moduleProxyTarget, priorityConfig: modulePriorityConfig } = configItem;
    if (!Array.isArray(apiNames) || apiNames.length === 0) {
        return;
    }
    const target = targetCallback();
    const masterAccount = window.isAccountBg && isElectron()
        ? systemApi.md5(lodashGet(systemApi.getMainAccount(), 'email', ''), true)
        : lodashGet(systemApi.getCurrentUser(), 'accountMd5', '');
    if (!masterAccount || !masterAccount.length) {
        return;
    }
    apiNames.forEach(apiConfig => {
        const apiname = typeof apiConfig === 'string' ? apiConfig : apiConfig.key;
        const proxyTarget = lodashGet(apiConfig, 'proxyTarget', moduleProxyTarget);
        const enableProxy = lodashGet(apiConfig, 'enableProxy', true);
        if (enableProxy === false) {
            return;
        }
        const originMethod = target[apiname];
        if (typeof originMethod !== 'function' || !masterDriver) {
            return;
        }
        const priorityConfig = lodashGet(apiConfig, 'priorityConfig', modulePriorityConfig);
        if (Array.isArray(priorityConfig) && priorityConfig.length > 0) {
            masterDriver.configureApiPriority({
                namespace,
                apiname,
            }, ...priorityConfig);
        }
        const winType = getWinEnv();
        if (typeof apiConfig !== 'string' && typeof apiConfig.backup === 'function') {
            masterDriver.interceptors.response.use({
                type: 'error',
                namespace: [namespace, apiname].join('.'),
                async resolve(res) {
                    return res;
                },
                reject: ((_namespace, _apiname) => async (error) => {
                    const _errNamepsace = lodashGet(error, 'config.namespace', '');
                    const _errApiName = lodashGet(error, 'config.apiname', '');
                    if (_errNamepsace !== _namespace || _errApiName !== _apiname) {
                        throw error;
                    }
                    return apiConfig.backup(error);
                })(namespace, apiname),
            }, 'high');
        }
        target[`_$${apiname}`] = originMethod;
        target[apiname] = new Proxy(originMethod, {
            apply(func, context, args) {
                var _a;
                const _account = ((_a = accountApi.getCurrentAccount()) === null || _a === void 0 ? void 0 : _a.email) || '';
                const globalAccount = _account && _account.length ? systemApi.md5(_account, true) : masterAccount;
                const isMainAccount = globalAccount === masterAccount;
                accountApi.setCurrentAccount({ email: '' });
                const targetWin = proxyTarget(winType, isMainAccount, isElectron() ? 'electron' : 'web');
                if (targetWin === null || targetWin === 'accountBg') {
                    return func.apply(context, args);
                }
                if (!masterDriver.getBridgeTempStatus()) {
                    masterDriver.enableBridgeOnce();
                    return func.apply(context, args);
                }
                const md5Email = masterAccount;
                return masterDriver.requestData({
                    namespace,
                    apiname,
                    args,
                }, md5Email);
            },
        });
    });
};
const initBridgeRegisterConfig = (workerDriver, configItem) => {
    if (proxyInitLockMap.get(`${configItem.namespace}-register`)) {
        return;
    }
    proxyInitLockMap.set(`${configItem.namespace}-register`, true);
    const currentEnv = getWinEnv();
    if (currentEnv === 'frontPage' || !inWindow()) {
        return;
    }
    const { apis, target: targetCallback, namespace } = configItem;
    if (!Array.isArray(apis) || apis.length === 0) {
        return;
    }
    const target = targetCallback();
    apis.forEach(_api => {
        if (typeof _api === 'string' || lodashGet(_api, 'funcname.length', 0) <= 0) {
            return;
        }
        workerDriver.registerResponseCMD(namespace, _api.key, args => {
            console.log('[bridge]detail.recept', namespace, _api, location.pathname);
            return target[_api.funcname](...args);
        });
    });
    workerDriver.registerCommonCMD(namespace, (apiname, args) => target[apiname](...args));
};
export const bridgeInit = () => {
    if (!inWindow() || process.env.BUILD_ISWEB) {
        return;
    }
    const systemApi = masterApi.getSystemApi();
    const eventApi = masterApi.getEventApi();
    if (lodashGet(systemApi.getCurrentUser(), 'accountMd5.length', 0) === 0) {
        const eid = eventApi.registerSysEventObserver('storeUserChangeEvent', {
            func: () => {
                const len = lodashGet(systemApi.getCurrentUser(), 'accountMd5.length', 0);
                if (len > 0) {
                    eventApi.unregisterSysEventObserver('storeUserChangeEvent', eid);
                    bridgeInit();
                }
            },
        });
        return;
    }
    if (!masterBridgeInstance || !workerBridgeInstance) {
        return;
    }
    bridgeProxyConfigList.forEach(implBridgeProxyConfig => {
        initBridgeProxyConfig(masterBridgeInstance, implBridgeProxyConfig);
        initBridgeRegisterConfig(workerBridgeInstance, implBridgeProxyConfig);
    });
    Object.values(interceptorConfigMap)
        .flat()
        .forEach(Interceptor => {
        const interceptor = new Interceptor([masterBridgeInstance.interceptors.request, masterBridgeInstance.interceptors.response]);
        console.log('[bridge]interceptorloaded', interceptor);
    });
    masterBridgeInstance === null || masterBridgeInstance === void 0 ? void 0 : masterBridgeInstance.createSubPageInWeb();
    workerBridgeInstance === null || workerBridgeInstance === void 0 ? void 0 : workerBridgeInstance.init();
    if (process.env.BUILD_ISELECTRON && systemApi.isMainWindow()) {
        masterApi.getEventApi().registerSysEventObserver('SubAccountWindowClosed', {
            func: async (ev) => {
                if (lodashGet(ev, 'eventData.subAccount.length', 0) === 0 || lodashGet(ev, 'eventData.webId.length', 0) === 0) {
                    return;
                }
                const { subAccount: closedEmail, webId } = ev.eventData;
                const accountApi = masterApi.requireLogicalApi(apis.accountApiImpl);
                const accountList = await accountApi.getSubAccounts({ expired: false });
                const isValidSubAccount = accountList.map(item => item.id).includes(closedEmail);
                if (!isValidSubAccount) {
                    return;
                }
                masterBridgeInstance.removeBridgeWin(webId);
            },
        });
    }
};
if (inWindow()) {
    window.bridgeApi = {
        master: masterBridgeInstance,
        worker: workerBridgeInstance,
    };
}
